import { useCallback, useEffect, useRef, useState } from 'react';
import { minigameApi } from '../services/api';
import { useGameStore } from '../stores/gameStore';
import { useMinigameAccessStore } from '../stores/minigameAccessStore';
import { sfx } from '../services/sfx';
import { requestInterstitialAd } from '../services/ads';
import { findMinigameView } from '../components/minigames/registry';
import type { MinigameActive, MinigameInfo, MinigameResult, MinigameSession } from '../types/minigame';

// Host screen for the minigame ladder: one new challenge every 5 player levels.
//
// This page owns everything generic — starting an attempt, the countdown, submitting,
// the result screen and the reward readout — so an individual minigame component only has
// to render its puzzle and call onSubmit. Nothing here decides an outcome: the server
// grades every submission (see MinigameFunctions).

function apiErr(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } | string } };
  if (typeof e?.response?.data === 'string') return e.response.data;
  return e?.response?.data?.message ?? fallback;
}

// 404 from an in-play action means the session is no longer there: the attempt has just been
// settled. Nothing the player can do about it, and nothing they need to be told.
function attemptGone(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 404;
}

function fmtClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function Minigames() {
  const player = useGameStore(s => s.player);
  const loadPlayer = useGameStore(s => s.loadPlayer);
  // Visibility and the publish right are decided by the server (staff see everything; normal
  // players only what an admin released). This avoids a pointless 403 round-trip and lets the
  // page explain itself if someone reaches the URL directly.
  const access = useMinigameAccessStore();

  const [list, setList] = useState<MinigameInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [publishing, setPublishing] = useState<string | null>(null);

  // Active attempt
  const [session, setSession] = useState<MinigameSession | null>(null);
  const [challenge, setChallenge] = useState<unknown>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MinigameResult | null>(null);
  // Set when the attempt on screen was picked back up rather than freshly started, so the
  // player is told why the clock is already part-way down.
  const [resumed, setResumed] = useState(false);
  const resumeCheckedRef = useRef(false);
  const submittedRef = useRef(false);
  // What to submit if the countdown expires — kept fresh by timed games via
  // setPendingSubmission, so a timeout still sends the player's real progress.
  const pendingRef = useRef<unknown>(null);
  // MUST be stable: views list it as an effect dependency, and this page re-renders every
  // second while the countdown runs. An inline arrow would re-fire their reset effects and
  // wipe the player's progress mid-attempt.
  const setPendingSubmission = useCallback((payload: unknown) => { pendingRef.current = payload; }, []);

  // An interstitial on the RESULT screen — the one real "between rounds" moment in the
  // game, and the reason Y8's 'next' placement fits here better than anywhere else: the
  // attempt is already settled and consumed server-side, and the player has stopped to
  // read how they did.
  //
  // It must never fire DURING an attempt. An interstitial is a full-screen overlay, and a
  // minigame's countdown is the SERVER's and is never paused — a break mid-attempt would
  // silently eat the player's remaining time and lose them the round. Keying the effect on
  // `result` is what guarantees that: a result exists only once the round is over.
  //
  // No de-duplication ref needed. `result` is a fresh object per attempt, so the effect
  // runs once per settled round; the portal's own rate limit absorbs both the StrictMode
  // double-invoke in dev and a player replaying immediately.
  useEffect(() => {
    if (!result) return;
    void requestInterstitialAd('minigame-result');
  }, [result]);

  const reload = useCallback(async () => {
    try {
      const res = await minigameApi.list();
      setList((res.data ?? []) as MinigameInfo[]);
    } catch (err) {
      setError(apiErr(err, 'Failed to load minigames'));
      setList([]);
    }
  }, []);

  useEffect(() => { if (!access.visible) return; loadPlayer(); reload(); }, [access.visible, loadPlayer, reload]);

  // An attempt spends its rewarded slot the moment it starts, so leaving the page — even by
  // a stray click — must not cost it. On opening, ask the server whether one is still in
  // flight and drop the player straight back into it. The remaining time comes from the
  // SERVER's clock: it never stopped while they were away (it can't, or stepping off the page
  // would be a free pause in the reflex games).
  useEffect(() => {
    if (!access.visible || resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;

    (async () => {
      try {
        const res = await minigameApi.active();
        const active = (res.data as { active?: MinigameActive | null })?.active;
        if (!active) return;
        if (!findMinigameView(active.minigameId)) return;   // nothing to render it with

        let parsed: unknown;
        try { parsed = JSON.parse(active.challenge); } catch { parsed = null; }
        if (!parsed || typeof parsed !== 'object') return;

        submittedRef.current = false;
        pendingRef.current = null;
        setResult(null);
        setChallenge(parsed);
        setSecondsLeft(active.secondsLeft);
        setResumed(true);
        setSession({
          sessionId: active.sessionId,
          minigameId: active.minigameId,
          name: active.name,
          challenge: active.challenge,
          timeLimitSec: active.timeLimitSec,
          rewarded: active.rewarded,
          attemptIndex: active.attemptIndex,
          rewardedLeft: 0,
          difficulty: active.difficulty,
          startedAgoMs: active.startedAgoMs,
        });
      } catch {
        // Nothing to resume, or the check failed — the ladder loads as usual.
      }
    })();
  }, [access.visible]);

  const submit = useCallback(async (submission: unknown) => {
    if (!session || submittedRef.current) return;
    submittedRef.current = true;
    setBusy(true);
    try {
      const res = await minigameApi.submit(
        session.minigameId, session.sessionId, JSON.stringify(submission ?? {}),
      );
      const r = res.data as MinigameResult;
      setError(null);
      setResult(r);
      if (r.success) sfx.success(); else sfx.fail();
      // The server may have granted rewards / levelled the player up.
      await loadPlayer();
      await reload();
    } catch (err) {
      setError(apiErr(err, 'Failed to submit the attempt'));
      submittedRef.current = false;   // let them retry a network failure
    } finally {
      setBusy(false);
    }
  }, [session, loadPlayer, reload]);

  // One in-play action (graded server-side). Returns the engine's payload to the view, or
  // null when the attempt resolved — in which case the host shows the result screen.
  const act = useCallback(async (action: unknown): Promise<unknown | null> => {
    if (!session || submittedRef.current) return null;
    // A polling view asks the server to project its clock forward two or three times a second.
    // That is not the player doing something, so it must not click: engines that poll were
    // beeping continuously for the whole attempt.
    const isPoll = typeof action === 'object' && action !== null
      && (action as { kind?: unknown }).kind === 'poll';
    setBusy(true);
    try {
      const res = await minigameApi.act(
        session.minigameId, session.sessionId, JSON.stringify(action ?? {}),
      );
      const d = res.data as {
        finished: boolean; resultJson?: string;
        success?: boolean; headline?: string | null; message?: string; reveal?: string | null; score?: number;
        rewarded?: boolean; credits?: number; experience?: number; reputation?: number;
      };
      let parsed: unknown = null;
      try { parsed = d.resultJson ? JSON.parse(d.resultJson) : null; } catch { parsed = null; }

      if (d.finished) {
        submittedRef.current = true;
        // Anything a racing request managed to report is moot now that there is a result.
        setError(null);
        setResult({
          success: !!d.success,
          headline: d.headline ?? null,
          message: d.message ?? '',
          reveal: d.reveal ?? null,
          score: d.score ?? 0,
          rewarded: !!d.rewarded,
          credits: d.credits ?? 0,
          experience: d.experience ?? 0,
          reputation: d.reputation ?? 0,
        });
        if (d.success) sfx.success(); else sfx.fail();
        await loadPlayer();
        await reload();
        return null;
      }
      if (!isPoll) sfx.click();
      return parsed;
    } catch (err) {
      // A rejected action (bad input) must not end the attempt — just report it. But a poll and a
      // player action are deliberately allowed to overlap, so the LAST burst of a round routinely
      // leaves a poll in flight that lands after the action settled the attempt and consumed the
      // session. Reporting that 404 put "This attempt has expired or was already submitted" under
      // the result screen at the end of every completed game.
      if (!submittedRef.current && !attemptGone(err)) setError(apiErr(err, 'Action rejected'));
      return null;
    } finally {
      setBusy(false);
    }
  }, [session, loadPlayer, reload]);

  // Countdown for timed games. Hitting zero submits whatever there is: the server marks it
  // a timeout, so the player always gets a result screen instead of a dead end.
  useEffect(() => {
    if (!session || result || session.timeLimitSec <= 0) return;
    if (secondsLeft <= 0) { void submit(pendingRef.current ?? { timedOut: true }); return; }
    const id = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(id);
  }, [session, result, secondsLeft, submit]);

  const start = async (game: MinigameInfo, ranked: boolean) => {
    setStarting(game.id);
    setError(null);
    try {
      sfx.click();
      // Stamped BEFORE the request: the session's clock starts on the server, so treating the
      // view's mount as t=0 left the client behind by a round-trip. A real-time engine then
      // believed it had more of the window left than the server did, and an answer given near
      // the end arrived past the server's deadline. Assuming the session began when the request
      // left errs the safe way — the client is never ahead.
      const requestedAt = Date.now();
      const res = await minigameApi.start(game.id, ranked);
      const s = res.data as MinigameSession;

      // Never swallow a bad payload into an empty object: the views fall back to their
      // defaults and the player is left staring at a board that does nothing. Surface it.
      let parsed: unknown;
      try { parsed = JSON.parse(s.challenge); } catch { parsed = null; }
      if (!parsed || typeof parsed !== 'object') {
        setError('The server sent an unreadable challenge — nothing was started.');
        return;
      }

      submittedRef.current = false;
      pendingRef.current = null;
      setResult(null);
      setResumed(false);
      setChallenge(parsed);
      setSecondsLeft(s.timeLimitSec);
      setSession({ ...s, startedAgoMs: Date.now() - requestedAt });
    } catch (err) {
      setError(apiErr(err, 'Could not start this minigame'));
    } finally {
      setStarting(null);
    }
  };

  const exitAttempt = () => {
    setSession(null);
    setChallenge(null);
    setResult(null);
    setResumed(false);
    submittedRef.current = false;
  };

  // Publish / withdraw (admin only). Refreshes both the list and the visibility flag, since
  // releasing the first minigame is what makes the whole feature appear for players.
  const togglePublished = async (game: MinigameInfo) => {
    if (!access.canPublish) return;
    setPublishing(game.id);
    setError(null);
    try {
      sfx.click();
      await minigameApi.setPublished(game.id, !game.published);
      await reload();
      await useMinigameAccessStore.getState().refresh();
    } catch (err) {
      setError(apiErr(err, 'Could not change the published state'));
    } finally {
      setPublishing(null);
    }
  };

  if (access.loaded && !access.visible) {
    return (
      <div style={{ maxWidth: 700 }}>
        <div className="card" style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)', padding: '2.5rem' }}>
          Minigames aren't open yet. Check back soon.
        </div>
      </div>
    );
  }

  // ── Active attempt ────────────────────────────────────────────────────────
  if (session) {
    // On a resumed attempt the list may not have arrived yet, so the session carries the name.
    const info = list?.find(m => m.id === session.minigameId);
    const title = info?.name ?? session.name ?? 'Minigame';
    const View = findMinigameView(session.minigameId);

    return (
      <div style={{ maxWidth: 900 }}>
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <h1 className="card-title" style={{ fontFamily: 'monospace', margin: 0 }}>{title}</h1>
              <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                {session.rewarded ? `Rewarded run · attempt ${session.attemptIndex}` : 'Practice run · no reward'}
                {!session.difficulty.softwareMet && ' · software gate not met (harder)'}
              </span>
            </div>
            {session.timeLimitSec > 0 && !result && (
              <span style={{
                fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 'bold',
                color: secondsLeft <= 10 ? '#f87171' : 'var(--primary-color)',
              }}>
                {fmtClock(Math.max(0, secondsLeft))}
              </span>
            )}
          </div>

          <div style={{ padding: '1rem' }}>
            {resumed && !result && (
              <p className="fx-fade-in" style={{
                margin: '0 0 0.75rem', fontFamily: 'monospace', fontSize: '0.78rem',
                color: '#fbbf24',
              }}>
                Attempt resumed — the clock kept running while you were away.
              </p>
            )}
            {result ? (
              <div className="fx-fade-in" style={{ fontFamily: 'monospace' }}>
                <h2 style={{ margin: '0 0 0.5rem', color: result.success ? '#4ade80' : '#f87171' }}>
                  {/* An endless entry reports its depth: cleared-or-failed is the wrong frame
                      when there is no win state. */}
                  {result.headline ?? (result.success ? 'CLEARED' : 'FAILED')}
                </h2>
                <p style={{ color: '#cbd5e1', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>{result.message}</p>
                {result.reveal && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{result.reveal}</p>
                )}
                {result.score > 0 && (
                  <p style={{ color: '#60a5fa', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>Score: {result.score.toLocaleString()}</p>
                )}
                {result.success && result.rewarded ? (
                  <p style={{ color: '#fbbf24', fontSize: '0.9rem', margin: '0.5rem 0' }}>
                    +{result.credits.toLocaleString()} cr · +{result.experience} XP · +{result.reputation} rep
                  </p>
                ) : result.success ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0.5rem 0' }}>
                    Practice run — no reward. Rewarded attempts reset at 00:00 UTC.
                  </p>
                ) : null}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  <button className="primary" onClick={exitAttempt} style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}>
                    Back to the list
                  </button>
                  {info && (
                    <>
                      {info.rewardedLeft > 0 && (
                        <button onClick={() => start(info, true)} style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}>
                          Play again ({info.rewardedLeft} rewarded left)
                        </button>
                      )}
                      <button onClick={() => start(info, false)} style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}>
                        Practice
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : View ? (
              <View
                challenge={challenge}
                secondsLeft={secondsLeft}
                startedAgoMs={session.startedAgoMs ?? 0}
                onSubmit={submit}
                onAct={act}
                setPendingSubmission={setPendingSubmission}
                busy={busy}
              />
            ) : (
              <div style={{ fontFamily: 'monospace', color: '#f87171' }}>
                This minigame has no interface yet.
                <button onClick={exitAttempt} style={{ marginLeft: '0.75rem', fontSize: '0.8rem', padding: '0.25rem 0.7rem' }}>Back</button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <p style={{ color: '#f87171', fontFamily: 'monospace', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</p>
        )}
      </div>
    );
  }

  // ── The ladder ────────────────────────────────────────────────────────────
  const level = player?.level ?? 1;
  const unlockedGames = (list ?? []).filter(m => m.unlocked);
  const upcoming = (list ?? []).filter(m => !m.unlocked);
  const nextUp = upcoming[0];

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 style={{ color: 'var(--primary-color)', marginBottom: '0.25rem' }}>Minigames</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.25rem', fontSize: '0.875rem' }}>
        A new challenge unlocks every 5 levels. Each one pays out for its first{' '}
        <strong>{list?.[0]?.rewardedPerDay ?? 3} attempts per day</strong> (with a decreasing
        bonus); after that you can keep playing as practice for free. The software you own
        sets the difficulty.
        {nextUp && <> Next unlock: <strong>{nextUp.name}</strong> at level {nextUp.unlockLevel} (you're {level}).</>}
      </p>

      {error && <p style={{ color: '#f87171', fontFamily: 'monospace', fontSize: '0.85rem' }}>{error}</p>}
      {list === null && <div className="loading">Loading minigames...</div>}

      {list !== null && unlockedGames.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          No minigames unlocked yet — the first one opens at <strong>level 5</strong>.
        </div>
      )}

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {unlockedGames.map(g => (
          <GameCard
            key={g.id}
            game={g}
            starting={starting === g.id}
            onStart={start}
            canPublish={access.canPublish}
            publishing={publishing === g.id}
            onTogglePublished={togglePublished}
          />
        ))}
      </div>

      {upcoming.length > 0 && (
        <>
          <h2 style={{ fontSize: '0.95rem', color: 'var(--text-muted)', margin: '1.75rem 0 0.75rem' }}>
            Locked — {upcoming.length} to go
          </h2>
          <div style={{ display: 'grid', gap: '0.4rem' }}>
            {upcoming.map(g => (
              <div key={g.id} className="card" style={{ padding: '0.6rem 0.85rem', opacity: 0.75 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <span style={{ color: '#cbd5e1' }}>
                    <strong>Lv {g.unlockLevel}</strong> · {g.name}
                    <span style={{ color: 'var(--text-muted)' }}> — {g.tagline}</span>
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {g.available ? `${g.genre}` : 'in development'}
                    </span>
                    {/* An admin releases a rung for players regardless of their OWN level. */}
                    {access.canPublish && (
                      <>
                        <span style={{
                          fontSize: '0.62rem', borderRadius: 3, padding: '0 4px',
                          color: g.published ? '#4ade80' : '#94a3b8',
                          border: `1px solid ${g.published ? '#4ade80' : '#5b6674'}`,
                        }}>
                          {g.published ? 'LIVE' : 'UNPUBLISHED'}
                        </span>
                        <button
                          onClick={() => togglePublished(g)}
                          disabled={publishing === g.id || (!g.available && !g.published)}
                          title={g.available
                            ? g.published ? 'Hide it from normal players again' : 'Make it visible to all players'
                            : 'Nothing to publish until it is implemented'}
                          style={{
                            fontSize: '0.7rem', padding: '0.2rem 0.55rem',
                            color: g.published ? '#fca5a5' : '#86efac',
                            borderColor: g.published ? '#7f1d1d' : '#166534',
                          }}
                        >
                          {publishing === g.id ? '…' : g.published ? 'Unpublish' : 'Publish'}
                        </button>
                      </>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Card for an unlocked minigame ────────────────────────────────────────────

function GameCard({ game, starting, onStart, canPublish, publishing, onTogglePublished }: {
  game: MinigameInfo;
  starting: boolean;
  onStart: (g: MinigameInfo, ranked: boolean) => void;
  /** Admin only: may this player release minigames? */
  canPublish: boolean;
  publishing: boolean;
  onTogglePublished: (g: MinigameInfo) => void;
}) {
  const gateText = game.anyCount > 0
    ? `${game.anyCount} tools at v${game.anyMinVersion.toFixed(1)}+`
    : game.requirements.map(r => `${r.type} v${r.minVersion.toFixed(1)}+`).join(' · ');

  return (
    <div className="card" style={{ borderLeft: `3px solid ${game.softwareMet ? '#4ade80' : '#fbbf24'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
            <span style={{ background: '#1f2937', color: '#9ca3af', borderRadius: 3, padding: '0 0.4rem', fontSize: '0.68rem', fontFamily: 'monospace' }}>
              Lv {game.unlockLevel}
            </span>
            <strong style={{ fontSize: '0.92rem' }}>{game.name}</strong>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontFamily: 'monospace' }}>{game.genre}</span>
            {!game.available && (
              <span style={{ color: '#fbbf24', fontSize: '0.7rem', fontFamily: 'monospace' }}>in development</span>
            )}
            {/* Release state — only staff ever see this line at all. */}
            {canPublish && (
              <span
                title={game.published ? 'Visible to all players' : 'Staff only'}
                style={{
                  fontSize: '0.62rem', fontFamily: 'monospace', borderRadius: 3, padding: '0 4px',
                  color: game.published ? '#4ade80' : '#94a3b8',
                  border: `1px solid ${game.published ? '#4ade80' : '#5b6674'}`,
                }}
              >
                {game.published ? 'LIVE' : 'UNPUBLISHED'}
              </span>
            )}
          </div>

          <p style={{ color: '#cbd5e1', fontSize: '0.82rem', margin: '0 0 0.4rem' }}>{game.tagline}</p>

          <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            <div>
              Gate: {gateText || 'none'}{' '}
              {game.softwareMet
                ? <span style={{ color: '#4ade80' }}>· met</span>
                : <span style={{ color: '#fbbf24' }}>· not met — playable, but harder</span>}
            </div>
            {game.requirements.length > 0 && (
              <div style={{ color: '#6b7280' }}>
                You own: {game.requirements.map(r =>
                  `${r.type} ${r.ownedVersion > 0 ? `v${r.ownedVersion.toFixed(1)}` : '—'}`).join(' · ')}
              </div>
            )}
            <div>
              Top payout: <span style={{ color: '#fbbf24' }}>
                {game.reward.credits.toLocaleString()}cr · {game.reward.experience} XP · {game.reward.reputation} rep
              </span>
              {game.timeLimitSec > 0 && <> · {game.timeLimitSec}s limit</>}
              {game.bestScore > 0 && <> · best {game.bestScore.toLocaleString()}</>}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', minWidth: '150px' }}>
          <button
            className="primary"
            disabled={!game.available || starting || game.rewardedLeft === 0}
            title={game.rewardedLeft === 0 ? 'No rewarded attempts left today' : undefined}
            onClick={() => onStart(game, true)}
            style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
          >
            {starting ? '…' : `Play (${game.rewardedLeft}/${game.rewardedPerDay})`}
          </button>
          <button
            disabled={!game.available || starting}
            onClick={() => onStart(game, false)}
            style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
          >
            Practice
          </button>
          {canPublish && (
            <button
              onClick={() => onTogglePublished(game)}
              disabled={publishing || (!game.available && !game.published)}
              title={game.available
                ? game.published ? 'Hide it from normal players again' : 'Make it visible to all players'
                : 'Nothing to publish until it is implemented'}
              style={{
                fontSize: '0.72rem', padding: '0.25rem 0.6rem',
                color: game.published ? '#fca5a5' : '#86efac',
                borderColor: game.published ? '#7f1d1d' : '#166534',
              }}
            >
              {publishing ? '…' : game.published ? 'Unpublish' : 'Publish'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
